import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LogOut, FolderOpen, Trash2, ArrowUpRight, Sparkles, BookOpen, Users, Settings as SettingsIcon, Shield, Loader2, RotateCcw, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { TextInput, TextArea, FieldLabel, PrimaryButton, SecondaryButton, Overline } from "@/components/ui-kit";
import CreateProjectFlow from "@/components/CreateProjectFlow";
import InterviewWizard from "@/components/InterviewWizard";
import CreativeWizard from "@/components/CreativeWizard";
import { WelcomeHero } from "@/components/WelcomeHero";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout, isSubscribed, tier, subscription, canCreateProject, cancelled } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const [interview, setInterview] = useState(false);
  const [creative, setCreative] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", image_url: "" });

  const [seedingDemo, setSeedingDemo] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get("/projects");
      setProjects(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    try {
      const res = await api.post("/projects", form);
      setProjects((p) => [res.data, ...p]);
      setCreating(false);
      setForm({ name: "", description: "", image_url: "" });
      nav(`/projects/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function onDelete(id) {
    if (!window.confirm(lang === "es" ? "¿Eliminar este proyecto?" : "Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function seedDemo() {
    setSeedingDemo(true);
    try {
      const res = await api.post("/projects/seed-demo");
      toast.success(lang === "es" ? "Proyecto demo cargado" : "Demo project loaded");
      await load();
      nav(`/projects/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSeedingDemo(false);
    }
  }

  function openPicker() {
    setPicker(true);
  }
  function choose(method) {
    setPicker(false);
    if (method === "manual") setCreating(true);
    else if (method === "interview") setInterview(true);
    else if (method === "creative") setCreative(true);
  }
  function onAIGenerated(project) {
    // Project already created by the backend; navigate to it.
    setInterview(false);
    setCreative(false);
    toast.success(lang === "es" ? "Plan generado" : "Plan generated");
    nav(`/projects/${project.id}`);
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="border-b border-[#E8E6E1] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="text-xl tracking-tight text-[#1A1A1A]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="brand-link"
          >
            Atelier <span className="italic font-normal text-[#B89154]">Business</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/catalog"
              className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-1.5"
              data-testid="header-catalog-link"
            >
              <BookOpen className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("catalog.go_to_catalog")}</span>
            </Link>
            <Link
              to="/pricing"
              className="hidden sm:inline text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
              data-testid="header-pricing-link"
            >
              {lang === "es" ? "Planes" : "Plans"}
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/admin/users"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
                data-testid="header-admin-link"
              >
                <Shield className="h-4 w-4" strokeWidth={1.5} />
                {lang === "es" ? "Admin" : "Admin"}
              </Link>
            )}
            <Link
              to="/settings"
              className="inline-flex items-center gap-1.5 text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
              data-testid="header-settings-link"
              aria-label={lang === "es" ? "Ajustes" : "Settings"}
            >
              <SettingsIcon className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden md:inline">{lang === "es" ? "Ajustes" : "Settings"}</span>
            </Link>
            <span className="hidden lg:inline text-sm text-[#5C5B5A]" data-testid="user-email">
              {user?.email}
            </span>
            <LanguageSwitcher />
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("auth.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Title */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <Overline>{lang === "es" ? "Portafolio" : "Portfolio"}</Overline>
            <h1
              className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-none"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              data-testid="dashboard-title"
            >
              {t("dashboard.title")}
            </h1>
            <p className="mt-4 text-base text-[#5C5B5A] max-w-xl">{t("dashboard.subtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {user?.role === "admin" && !projects.some((p) => p.name === "Golden Years Abroad" && p.is_owner !== false) && (
              <SecondaryButton onClick={seedDemo} disabled={seedingDemo} testId="seed-demo-btn">
                {seedingDemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" strokeWidth={1.5} />}
                {lang === "es" ? "Cargar demo GYA (admin)" : "Load demo (GYA · admin)"}
              </SecondaryButton>
            )}
            {canCreateProject ? (
              <PrimaryButton onClick={openPicker} testId="new-project-btn">
                <Sparkles className="h-4 w-4" strokeWidth={2} />
                {t("dashboard.new_project")}
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={() => nav("/pricing")} testId="new-project-locked-btn">
                <Lock className="h-4 w-4" strokeWidth={2} />
                {cancelled
                  ? (lang === "es" ? "Reactivar suscripción" : "Reactivate subscription")
                  : (lang === "es" ? "Ver planes" : "See plans")}
              </PrimaryButton>
            )}
          </div>
        </div>

        {/* Tier usage banner */}
        {user && subscription && (
          <UsageBanner
            tier={tier}
            subscription={subscription}
            cancelled={cancelled}
            lang={lang}
          />
        )}

        {/* Free-tier upgrade banner */}
        {!isSubscribed && !cancelled && tier === "free" && (
          <Link
            to="/pricing"
            className="group mb-6 border border-[#B89154]/40 bg-[#F5EFE6] rounded-sm px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-[#B89154] transition-colors"
            data-testid="dashboard-upgrade-banner"
          >
            <div className="flex items-start gap-3">
              <Lock className="h-4 w-4 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">
                {lang === "es"
                  ? "Plan gratuito: 2 lecturas del catálogo + 1 business plan propio (edición manual). Activa Starter (49,90 €/mes) por IA + 6 plantillas o Pro (99,90 €/mes) por acceso ilimitado."
                  : "Free plan: 2 catalog reads + 1 own plan (manual edits). Activate Starter ($49.90/mo) for AI + 6 templates or Pro ($99.90/mo) for unlimited access."}
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#2563EB] shrink-0 group-hover:gap-2.5 transition-all">
              {lang === "es" ? "Ver planes" : "See plans"}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </Link>
        )}

        {/* Cancelled-subscription banner */}
        {cancelled && (
          <Link
            to="/pricing"
            className="group mb-6 border border-[#B89154]/50 bg-[#F5EFE6] rounded-sm px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-[#B89154] transition-colors"
            data-testid="dashboard-cancelled-banner"
          >
            <div className="flex items-start gap-3">
              <Lock className="h-4 w-4 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">
                {lang === "es"
                  ? "Tu suscripción está cancelada. Puedes seguir editando manualmente tus planes, pero la IA y la creación de nuevos planes están desactivadas. Reactiva para volver a usar todas las herramientas."
                  : "Your subscription is cancelled. You can still manually edit your plans, but AI and new plan creation are off. Reactivate to use every tool again."}
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#2563EB] shrink-0 group-hover:gap-2.5 transition-all">
              {lang === "es" ? "Reactivar" : "Reactivate"}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </Link>
        )}

        {/* Inspiration catalog banner */}
        <Link
          to="/catalog"
          className="group block mb-10 text-white rounded-2xl overflow-hidden shadow-sm transition-all hover:shadow-xl hover:brightness-105"
          style={{ backgroundImage: "linear-gradient(120deg, #2563EB, #7C3AED)" }}
          data-testid="catalog-banner-link"
        >
          <div className="px-6 lg:px-10 py-6 lg:py-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="bg-white/15 border border-white/25 rounded-xl p-3 shrink-0 backdrop-blur-sm">
                <BookOpen className="h-6 w-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/85 font-semibold">
                  {lang === "es" ? "Inspiración" : "Inspiration"}
                </div>
                <h2
                  className="mt-1 text-2xl text-white"
                  style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                >
                  {t("catalog.title")}
                </h2>
                <p className="mt-1 text-sm text-white/85 max-w-xl">{t("catalog.dashboard_cta")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-white shrink-0 group-hover:gap-3 transition-all">
              {t("catalog.open_button")}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </div>
          </div>
        </Link>

        {/* Project grid */}
        {loading ? (
          <div className="text-[#8A8886] text-sm">{t("common.loading")}</div>
        ) : projects.length === 0 ? (
          <>
            <WelcomeHero
              lang={lang}
              canCreate={canCreateProject}
              onStartBlank={openPicker}
            />
            {/* Compact fallback empty state — kept for users who dismissed
                the hero, and for the admin "Load demo GYA" affordance. */}
            <div
              className="border border-dashed border-[#E8E6E1] rounded-sm p-8 text-center"
              data-testid="empty-state"
            >
              <p className="text-sm text-[#8A8886]">
                {t("dashboard.empty_desc")}
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                {canCreateProject ? (
                  <PrimaryButton onClick={openPicker} testId="empty-new-project-btn">
                    <Sparkles className="h-4 w-4" strokeWidth={2} />
                    {t("dashboard.new_project")}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton onClick={() => nav("/pricing")} testId="empty-new-project-locked-btn">
                    <Lock className="h-4 w-4" strokeWidth={2} />
                    {cancelled
                      ? (lang === "es" ? "Reactivar suscripción" : "Reactivate subscription")
                      : (lang === "es" ? "Ver planes" : "See plans")}
                  </PrimaryButton>
                )}
                {user?.role === "admin" && (
                  <SecondaryButton onClick={seedDemo} disabled={seedingDemo} testId="empty-seed-demo-btn">
                    {seedingDemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" strokeWidth={1.5} />}
                    {lang === "es" ? "Cargar demo GYA (admin)" : "Load demo (GYA · admin)"}
                  </SecondaryButton>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <article
                key={p.id}
                className="group bg-white border border-[#E8E6E1] rounded-sm overflow-hidden hover:shadow-sm hover:-translate-y-[1px] transition-all relative"
                data-testid={`project-card-${p.id}`}
              >
                {p.is_owner === false && (
                  <div
                    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-[#B89154]/40 text-[#8a6a37] text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-sm shadow-sm"
                    data-testid={`shared-badge-${p.id}`}
                  >
                    <Users className="h-3 w-3" strokeWidth={1.5} />
                    {lang === "es" ? "Compartido" : "Shared"}
                  </div>
                )}
                <Link to={`/projects/${p.id}`} className="block">
                  <div className="aspect-[4/3] bg-[#F5EFE6] relative overflow-hidden">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#B89154]">
                        <FolderOpen className="h-12 w-12" strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <Overline>
                      {new Date(p.created_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Overline>
                    <h3
                      className="mt-2 text-2xl tracking-tight text-[#1A1A1A] leading-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                    >
                      {p.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#5C5B5A] line-clamp-3 min-h-[3rem]">
                      {p.description || "—"}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center justify-between border-t border-[#E8E6E1] px-6 py-3">
                  <Link
                    to={`/projects/${p.id}`}
                    className="inline-flex items-center gap-1 text-sm text-[#2563EB] hover:text-[#1D4ED8]"
                    data-testid={`open-project-${p.id}-btn`}
                  >
                    {t("dashboard.open")}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                  {p.is_owner !== false ? (
                    <button
                      onClick={() => onDelete(p.id)}
                      className="text-[#8A8886] hover:text-[#9E2A2B] transition-colors"
                      data-testid={`delete-project-${p.id}-btn`}
                      aria-label="delete"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886]" data-testid={`role-badge-${p.id}`}>
                      {lang === "es" ? "Editor" : "Editor"}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Method picker */}
      {picker && <CreateProjectFlow onClose={() => setPicker(false)} onChoose={choose} />}

      {/* Interview wizard */}
      {interview && (
        <InterviewWizard
          onClose={() => setInterview(false)}
          onCreated={onAIGenerated}
        />
      )}

      {/* Creative wizard */}
      {creative && (
        <CreativeWizard
          onClose={() => setCreative(false)}
          onCreated={onAIGenerated}
        />
      )}

      {/* Manual create modal */}
      {creating && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1A1A]/40 backdrop-blur-sm p-4"
          onClick={() => setCreating(false)}
          data-testid="create-project-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={onCreate}
            className="w-full max-w-lg bg-white border border-[#E8E6E1] rounded-sm p-8 space-y-5"
          >
            <h2
              className="text-2xl tracking-tight text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {t("modal.create_project")}
            </h2>
            <div>
              <FieldLabel>{t("modal.project_name")}</FieldLabel>
              <TextInput
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testId="modal-project-name-input"
                required
              />
            </div>
            <div>
              <FieldLabel>{t("modal.project_desc")}</FieldLabel>
              <TextArea
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                testId="modal-project-desc-input"
                rows={3}
              />
            </div>
            <div>
              <FieldLabel>{t("modal.project_image")}</FieldLabel>
              <TextInput
                value={form.image_url}
                onChange={(v) => setForm({ ...form, image_url: v })}
                testId="modal-project-image-input"
                placeholder="https://..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <SecondaryButton onClick={() => setCreating(false)} testId="modal-cancel-btn">
                {t("modal.cancel")}
              </SecondaryButton>
              <PrimaryButton type="submit" testId="modal-create-btn">
                {t("modal.create")}
              </PrimaryButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// UsageBanner — tier-aware quota indicator (Starter slot count, Pro monthly,
// Free lifetime). Hidden for cancelled (we already render a dedicated banner)
// and admins.
// ---------------------------------------------------------------------------
function UsageBanner({ tier, subscription, cancelled, lang }) {
  if (cancelled) return null;
  if (!subscription) return null;
  if (tier === "free") return null; // already shown via the free upsell banner
  const monthlyUsed = subscription.monthly_plans_used ?? 0;
  const monthlyLimit = subscription.monthly_plan_limit;
  const lifetimeUsed = subscription.lifetime_plans_used ?? 0;
  const lifetimeLimit = subscription.lifetime_plan_limit;
  const slotsUsed = subscription.selected_slots_used ?? 0;
  const slotsTotal = subscription.selected_slots_total ?? 6;

  if (tier === "starter") {
    return (
      <div
        className="mb-6 border border-[#2563EB]/25 bg-[#EFF4FF] rounded-sm px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        data-testid="dashboard-starter-usage"
      >
        <p className="text-sm text-[#1A1A1A]">
          {lang === "es"
            ? `Starter · ${lifetimeUsed}/${lifetimeLimit ?? 1} plan · ${slotsUsed}/${slotsTotal} plantillas elegidas`
            : `Starter · ${lifetimeUsed}/${lifetimeLimit ?? 1} plan · ${slotsUsed}/${slotsTotal} templates picked`}
        </p>
        <a href="/pricing" className="text-sm text-[#2563EB] underline shrink-0" data-testid="starter-upgrade-link">
          {lang === "es" ? "Sube a Pro — ilimitado" : "Upgrade to Pro — unlimited"}
        </a>
      </div>
    );
  }
  if (tier === "pro") {
    return (
      <div
        className="mb-6 border border-[#2563EB]/25 bg-[#EFF4FF] rounded-sm px-6 py-3 flex items-center gap-3"
        data-testid="dashboard-pro-usage"
      >
        <p className="text-sm text-[#1A1A1A]">
          {lang === "es"
            ? `Pro · ${monthlyUsed}/${monthlyLimit ?? 10} planes este mes · acceso ilimitado a plantillas`
            : `Pro · ${monthlyUsed}/${monthlyLimit ?? 10} plans this month · unlimited templates`}
        </p>
      </div>
    );
  }
  return null;
}
